/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { LinkProps } from "@i18n/next";
import { Link } from "@i18n/next";
import type { AnalyticsEvent } from "@packages/analytics";
import { useAnalytics } from "@packages/analytics";
import { usePathMatch } from "@packages/sdk";
import type { TabBarIcon } from "@packages/ui";
import { Button, numericPixels, radius, spacing, useHover } from "@packages/ui";
import type { PropsWithChildren, ReactNode } from "react";
import { forwardRef } from "react";

const buttonStyles: Interpolation = {
  borderRadius: radius.s,
  height: spacing.xxl,
  justifyContent: "center",
  padding: numericPixels[0],
  width: numericPixels[104],
};

// flex (not block) avoids the inline box's baseline descender gap pushing the icon off-centre.
const hoverContentStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
};

type MenuBarPageProps = PropsWithChildren<LinkProps> & {
  icon?: TabBarIcon;
  override?: ReactNode;
  analyticsEvent?: AnalyticsEvent;
};

export const MenuBarPage = forwardRef<any, MenuBarPageProps>(
  ({ children, href, icon, override, analyticsEvent, ...props }, ref) => {
    const { hoverProps, isHovered } = useHover({});
    const analytics = useAnalytics();
    const matcher = usePathMatch();
    const Icon =
      icon &&
      (matcher(
        href === "/home" ? "/" : href.toString(),
        href.toString() === "/" || href.toString() === "/home",
      )
        ? icon.Filled
        : icon.Outline);

    return (
      <Button
        asChild
        ref={ref}
        css={buttonStyles}
        textProps={{ size: "s", type: "title" }}
        variant="transparentPrimary"
        {...hoverProps}
      >
        <Link
          href={href}
          onClick={() => analyticsEvent && analytics.track(analyticsEvent)}
          prefetch={false}
          {...props}
        >
          <span css={hoverContentStyles}>
            {isHovered ? children : (override ?? <Icon />)}
          </span>
        </Link>
      </Button>
    );
  },
);

MenuBarPage.displayName = "MenuBarPage";
